/**
 * Converts a logo URL to base64 data URL using fetch API to avoid CORS issues
 */
export async function convertLogoUrlToBase64(url: string): Promise<string> {
  try {
    // Fetch the image as a blob
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    
    // Convert blob to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = () => {
        reject(new Error('Failed to convert blob to base64'));
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Fetch method failed, trying canvas method:', error);
    
    // Fallback to canvas method
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          // Set canvas size to match image
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw image to canvas
          ctx.drawImage(img, 0, 0);
          
          // Convert to base64
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error(`Failed to load image: ${url}`));
      };
      
      img.src = url;
    });
  }
}

/**
 * Checks if a string is a base64 data URL
 */
export function isBase64DataUrl(str: string): boolean {
  return str.startsWith('data:image/');
}

/**
 * Converts logo to base64 if it's a URL, otherwise returns as-is
 */
export async function ensureLogoIsBase64(logo: string): Promise<string> {
  if (!logo) return '';
  
  if (isBase64DataUrl(logo)) {
    return logo;
  }
  
  try {
    return await convertLogoUrlToBase64(logo);
  } catch (error) {
    console.warn('Failed to convert logo URL to base64:', error);
    return logo; // Return original URL as fallback
  }
}
